import { useEffect, useState, type FormEvent } from 'react';
import {
  listAddressBook,
  createAddressBookEntry,
  updateAddressBookEntry,
  deleteAddressBookEntry,
  type AddressBookEntry,
} from '../api/addressBook';
import { HttpError } from '../api/client';
import { AddressSearch } from '../components/AddressSearch';
import { SettingsTabs } from '../components/SettingsTabs';
import { type Geo } from '../api/geocoding';
import { parseCoordinate, normalizeCoordinateInput } from '../utils/coordinates';
import { getNavPreferences, loadNavPreferences, saveNavPreferences } from '../utils/navigate';
import type { NavAppMobile, NavAppDesktop } from '@camper-diary/shared';

type Mode = { kind: 'idle' } | { kind: 'creating' } | { kind: 'editing'; entry: AddressBookEntry };

export function SettingsPage() {
  const [entries, setEntries] = useState<AddressBookEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>({ kind: 'idle' });
  const [busy, setBusy] = useState(false);

  async function refresh() {
    try {
      setEntries(await listAddressBook());
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore');
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  function startCreate() {
    setMode({ kind: 'creating' });
  }
  function startEdit(entry: AddressBookEntry) {
    setMode({ kind: 'editing', entry });
  }
  function cancelForm() {
    setMode({ kind: 'idle' });
  }

  async function onDelete(entry: AddressBookEntry) {
    if (!confirm(`Eliminare "${entry.label}" dalla rubrica?`)) return;
    setBusy(true);
    try {
      await deleteAddressBookEntry(entry.id);
      await refresh();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Errore');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <div className="page-head">
        <div className="title-block">
          <h1>Impostazioni</h1>
        </div>
      </div>

      <SettingsTabs />

      <NavigatorSettings />

      <section className="address-book-section">
        <div className="section-head">
          <h2>📕 Rubrica indirizzi</h2>
          {mode.kind === 'idle' && (
            <button className="btn btn-primary" onClick={startCreate} disabled={busy}>
              + Aggiungi voce
            </button>
          )}
        </div>
        <p className="muted">
          Salva qui i luoghi che usi spesso (Casa, Lavoro, Suoceri…). Potrai richiamarli con un
          click quando crei una tappa di un viaggio o di una pianificazione.
        </p>

        {error && <p className="err">{error}</p>}

        {mode.kind === 'creating' && (
          <AddressBookForm
            onCancel={cancelForm}
            onSubmit={async (data) => {
              await createAddressBookEntry(data);
              cancelForm();
              await refresh();
            }}
          />
        )}

        {mode.kind === 'editing' && (
          <AddressBookForm
            initial={mode.entry}
            onCancel={cancelForm}
            onSubmit={async (data) => {
              await updateAddressBookEntry(mode.entry.id, data);
              cancelForm();
              await refresh();
            }}
          />
        )}

        {!entries && <p className="muted">Caricamento…</p>}

        {entries && entries.length === 0 && mode.kind === 'idle' && (
          <p className="muted">La rubrica è vuota. Aggiungi la prima voce.</p>
        )}

        {entries && entries.length > 0 && (
          <ul className="address-book-list">
            {entries.map((e) => (
              <li key={e.id} className="address-book-item">
                <div className="item-body">
                  <h3>{e.label}</h3>
                  {e.address && <p className="muted">{e.address}</p>}
                  <p className="coords">
                    {e.lat.toFixed(5)}, {e.lon.toFixed(5)}
                  </p>
                  {e.notes && <p className="notes">📝 {e.notes}</p>}
                </div>
                <div className="item-actions">
                  <button
                    className="btn btn-ghost"
                    disabled={busy}
                    onClick={() => startEdit(e)}
                  >
                    Modifica
                  </button>
                  <button
                    className="btn btn-danger"
                    disabled={busy}
                    onClick={() => void onDelete(e)}
                  >
                    Elimina
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function NavigatorSettings() {
  const initial = getNavPreferences();
  const [mobilePref, setMobilePref] = useState<NavAppMobile>(initial.navAppMobile);
  const [desktopPref, setDesktopPref] = useState<NavAppDesktop>(initial.navAppDesktop);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void loadNavPreferences()
      .then((p) => {
        setMobilePref(p.navAppMobile);
        setDesktopPref(p.navAppDesktop);
      })
      .catch(() => {});
  }, []);

  async function changeMobile(v: NavAppMobile) {
    const prev = mobilePref;
    setMobilePref(v);
    setSaving(true);
    setError(null);
    try {
      await saveNavPreferences({ navAppMobile: v });
    } catch {
      setMobilePref(prev);
      setError('Impossibile salvare l’impostazione. Riprova.');
    } finally {
      setSaving(false);
    }
  }
  async function changeDesktop(v: NavAppDesktop) {
    const prev = desktopPref;
    setDesktopPref(v);
    setSaving(true);
    setError(null);
    try {
      await saveNavPreferences({ navAppDesktop: v });
    } catch {
      setDesktopPref(prev);
      setError('Impossibile salvare l’impostazione. Riprova.');
    } finally {
      setSaving(false);
    }
  }

  // Su mobile mostriamo entrambe le opzioni specifiche (Apple Maps su iOS,
  // selettore di sistema su Android): le coordinate aprono comunque la stessa
  // app, quindi le elenchiamo entrambe e l'utente sceglie quella che ha.
  const mobileOptions: { value: NavAppMobile; label: string }[] = [
    { value: 'auto', label: 'Automatico (prova Waze, poi il navigatore di sistema)' },
    { value: 'waze', label: 'Waze' },
    { value: 'google', label: 'Google Maps' },
    { value: 'apple', label: 'Apple Maps (iOS)' },
    { value: 'system', label: 'Selettore di sistema (Android)' },
  ];

  const desktopOptions: { value: NavAppDesktop; label: string }[] = [
    { value: 'google', label: 'Google Maps' },
    { value: 'waze', label: 'Waze (web)' },
    { value: 'osm', label: 'OpenStreetMap' },
  ];

  return (
    <section className="navigator-section">
      <div className="section-head">
        <h2>🧭 Navigatore</h2>
      </div>
      <p className="muted">
        Scegli quale app/sito aprire quando premi "Naviga" su una tappa o un POI. Le impostazioni
        sono salvate sull'account: valgono da qualsiasi dispositivo.
      </p>

      {error && <p className="err">{error}</p>}

      <h3>📱 Da telefono</h3>
      <div className="nav-options">
        {mobileOptions.map((o) => (
          <label key={o.value} className="nav-option">
            <input
              type="radio"
              name="navAppMobile"
              checked={mobilePref === o.value}
              disabled={saving}
              onChange={() => void changeMobile(o.value)}
            />
            <span>{o.label}</span>
          </label>
        ))}
      </div>

      <h3>💻 Da computer</h3>
      <div className="nav-options">
        {desktopOptions.map((o) => (
          <label key={o.value} className="nav-option">
            <input
              type="radio"
              name="navAppDesktop"
              checked={desktopPref === o.value}
              disabled={saving}
              onChange={() => void changeDesktop(o.value)}
            />
            <span>{o.label}</span>
          </label>
        ))}
      </div>
    </section>
  );
}

type FormData = {
  label: string;
  lat: number;
  lon: number;
  address: string | null;
  notes: string | null;
};

function AddressBookForm({
  initial,
  onSubmit,
  onCancel,
}: {
  initial?: AddressBookEntry;
  onSubmit: (data: FormData) => Promise<void>;
  onCancel: () => void;
}) {
  const [label, setLabel] = useState(initial?.label ?? '');
  const [address, setAddress] = useState(initial?.address ?? '');
  const [lat, setLat] = useState(initial?.lat.toString() ?? '');
  const [lon, setLon] = useState(initial?.lon.toString() ?? '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function onAddressPick(r: Geo) {
    setLat(r.lat.toFixed(6));
    setLon(r.lon.toFixed(6));
    setAddress(r.displayName);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const latNum = parseCoordinate(lat);
    const lonNum = parseCoordinate(lon);
    if (latNum === null || latNum < -90 || latNum > 90) {
      setError('Latitudine non valida (deve essere tra -90 e 90)');
      return;
    }
    if (lonNum === null || lonNum < -180 || lonNum > 180) {
      setError('Longitudine non valida (deve essere tra -180 e 180)');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        label: label.trim(),
        lat: latNum,
        lon: lonNum,
        address: address.trim() || null,
        notes: notes.trim() || null,
      });
    } catch (err) {
      setError(err instanceof HttpError ? err.apiError.message : 'Errore di connessione');
      setSubmitting(false);
    }
  }

  return (
    <form className="form-card" onSubmit={handleSubmit}>
      <label>
        <span>Etichetta * (es. "Casa")</span>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          required
          autoFocus
          maxLength={80}
        />
      </label>

      <label>
        <span>Cerca luogo / indirizzo</span>
        <AddressSearch value={address} onChange={setAddress} onPick={onAddressPick} />
      </label>

      <div className="row">
        <label>
          <span>Latitudine *</span>
          <input
            type="text"
            inputMode="decimal"
            value={lat}
            onChange={(e) => setLat(e.target.value)}
            onBlur={(e) => setLat(normalizeCoordinateInput(e.target.value))}
            required
          />
        </label>
        <label>
          <span>Longitudine *</span>
          <input
            type="text"
            inputMode="decimal"
            value={lon}
            onChange={(e) => setLon(e.target.value)}
            onBlur={(e) => setLon(normalizeCoordinateInput(e.target.value))}
            required
          />
        </label>
      </div>

      <label>
        <span>Note</span>
        <textarea
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={2000}
        />
      </label>

      {error && <p className="err">{error}</p>}

      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? 'Salvataggio…' : initial ? 'Salva modifiche' : 'Crea voce'}
        </button>
        <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={submitting}>
          Annulla
        </button>
      </div>
    </form>
  );
}
